"use client";

import React, { useEffect, useRef, useState } from "react";
import { MapPin, Phone, Mail, ArrowUpRight, Clock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const officeHours = [{
  day: "Monday",
  hours: "10:00 AM - 10:00 PM"
}, {
  day: "Tuesday",
  hours: "10:00 AM - 10:00 PM"
}, {
  day: "Wednesday",
  hours: "10:00 AM - 10:00 PM"
}, {
  day: "Thursday",
  hours: "10:00 AM - 10:00 PM"
}, {
  day: "Friday",
  hours: "10:00 AM - 12:00 AM"
}, {
  day: "Saturday",
  hours: "12:00 PM - 12:00 AM"
}, {
  day: "Sunday",
  hours: "By Appointment"
}];
export default function OurOffices() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll();
  const {
    ref: tableRef,
    isVisible: isTableVisible
  } = useRevealOnScroll();
  return <section data-section-id="234928" id="find-the-studio" className="bg-background text-foreground py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        {}
        <div ref={headerRef} className={`mb-16 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-5xl font-default font-bold text-foreground tracking-tighter mb-4">
            Find the Studio
          </h2>
          <div className="w-24 h-1 bg-primary shadow-[0_0_15px_rgba(0,255,255,0.5)]"></div>
        </div>

        {}
        <div ref={gridRef} className={`grid grid-cols-1 lg:grid-cols-2 gap-0 border border-border rounded-3xl overflow-hidden transition-all duration-1000 delay-200 ease-out ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {}
          <div className="relative h-[400px] lg:h-auto bg-muted border-b lg:border-b-0 lg:border-r border-border overflow-hidden group">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655264/img/pulsewave_studio_map.png" alt="Map showing PulseWave Studios location in the creative district" className="absolute inset-0 w-full h-full object-cover grayscale contrast-150 opacity-60 transition-transform duration-1000 group-hover:scale-105" />
            {}
            <div className="absolute inset-0 bg-background/40 mix-blend-multiply pointer-events-none"></div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
              <div className="relative">
                <div className="absolute inset-0 bg-primary rounded-full animate-ping opacity-40"></div>
                <MapPin className="w-12 h-12 text-primary fill-background relative z-10 drop-shadow-[0_0_10px_rgba(0,255,255,0.8)]" />
              </div>
              <div className="mt-3 bg-card/90 backdrop-blur-md border border-primary/30 rounded-full px-4 py-2 shadow-xl">
                <span className="text-xs font-mono font-bold tracking-widest uppercase text-primary">
                  PULSEWAVE HQ
                </span>
              </div>
            </div>
          </div>

          {}
          <div className="flex flex-col justify-center p-8 md:p-16 bg-card">
            <div className="space-y-12">
              {}
              <div className="flex items-start gap-6">
                <div className="p-3 bg-secondary/50 border border-border rounded-xl shrink-0">
                  <MapPin className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-xs font-mono font-bold tracking-widest uppercase text-muted-foreground mb-3">
                    Studio Location
                  </h3>
                  <p className="text-xl md:text-2xl font-default font-medium text-foreground leading-snug">
                    808 Rhythm Lane
                    <br />
                    Sonic District
                    <br />
                    Sound City
                  </p>
                  <Button variant="link" className="p-0 h-auto mt-4 text-primary font-default font-medium hover:text-primary/80 transition-colors" asChild>
                    <Link to="https://maps.google.com" newTab>
                      Get Directions <ArrowUpRight className="w-4 h-4 ml-1" />
                    </Link>
                  </Button>
                </div>
              </div>

              {}
              <div className="flex items-start gap-6">
                <div className="p-3 bg-secondary/50 border border-border rounded-xl shrink-0">
                  <Phone className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-xs font-mono font-bold tracking-widest uppercase text-muted-foreground mb-3">
                    Call Studio
                  </h3>
                  <p className="text-xl md:text-2xl font-default font-medium text-foreground">
                    +1 (555) PULSE-WV
                  </p>
                </div>
              </div>

              {}
              <div className="flex items-start gap-6">
                <div className="p-3 bg-secondary/50 border border-border rounded-xl shrink-0">
                  <Mail className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-xs font-mono font-bold tracking-widest uppercase text-muted-foreground mb-3">
                    Session Inquiries
                  </h3>
                  <p className="text-lg md:text-xl font-default font-medium text-foreground break-all">
                    sessions@pulsewave.studio
                  </p>
                  <Button variant="link" className="p-0 h-auto mt-4 text-primary font-default font-medium hover:text-primary/80 transition-colors" asChild>
                    <Link to="/ContactPage">
                      Book a Session <ArrowUpRight className="w-4 h-4 ml-1" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {}
        <div ref={tableRef} className={`mt-24 transition-all duration-1000 delay-400 ease-out ${isTableVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex items-center gap-3 mb-8">
            <Clock className="w-6 h-6 text-primary" />
            <h3 className="text-2xl font-default font-bold text-foreground tracking-tight">
              Studio Availability
            </h3>
          </div>
          
          <div className="border border-border bg-card rounded-2xl overflow-hidden shadow-2xl">
            <Table>
              <TableHeader className="bg-muted/50">
                <TableRow className="hover:bg-transparent border-border">
                  <TableHead className="w-1/2 py-5 px-6 text-xs font-mono font-bold uppercase tracking-widest text-muted-foreground">
                    Day
                  </TableHead>
                  <TableHead className="w-1/2 py-5 px-6 text-xs font-mono font-bold uppercase tracking-widest text-muted-foreground text-right">
                    Hours
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {officeHours.map((schedule, i) => <TableRow key={schedule.day} data-index={i} className="border-border hover:bg-primary/5 transition-colors">
                    <TableCell className="py-4 px-6 font-default font-medium text-foreground">
                      {schedule.day}
                    </TableCell>
                    <TableCell className="py-4 px-6 font-mono text-sm text-primary text-right">
                      {schedule.hours}
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </section>;
}